'use client';

import { useRouter } from 'next/navigation';
import NextLink from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { BotyLogo } from '@boty/ui';
import { useAuth } from '@boty/database';
import { LanguageSwitcher } from '../layout/LanguageSwitcher';
import { UserMenu } from '../layout/UserMenu';
import type { LandingHeaderProps } from './types';

export type { LandingHeaderProps };

/**
 * LandingHeader - Minimal header for the landing page
 *
 * Features:
 * - Boty logo that links to home
 * - Sign in button
 * - Language switcher (Hebrew/English/Spanish)
 * - Transparent background, no elevation
 * - Responsive layout
 *
 * Story 6.7: Landing Page Header And Footer For Credibility
 */
export function LandingHeader({
  showSignIn = true,
  showLanguageSwitcher = true,
}: LandingHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const locale = useLocale();
  const router = useRouter();
  const t = useTranslations('creator.landing.header');
  const tLanding = useTranslations('creator.landing');
  const { user } = useAuth();
  const isAuthenticated = !!user;

  const handleLogoClick = () => {
    router.push(`/${locale}`);
  };

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        top: 0,
        zIndex: 1100,
        bgcolor: 'background.default',
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Logo */}
        <Box
          onClick={handleLogoClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <BotyLogo size={isMobile ? 'small' : 'medium'} />
        </Box>

        {/* Right side: Language Switcher + Auth controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          {/* Language Switcher */}
          {showLanguageSwitcher && <LanguageSwitcher variant="minimal" />}

          {/* Authenticated: My Botys link + User Menu */}
          {isAuthenticated ? (
            <>
              <Button
                component={NextLink}
                href={`/${locale}/dashboard`}
                variant="text"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {tLanding('goToDashboard')}
              </Button>
              <UserMenu />
            </>
          ) : (
            /* Not authenticated: Sign In Button */
            showSignIn && (
              <Button
                component={NextLink}
                href={`/${locale}/quick-signup`}
                variant="outlined"
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  minWidth: { xs: 'auto', sm: 100 },
                }}
              >
                {t('signIn')}
              </Button>
            )
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default LandingHeader;
